import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import axios from 'axios';
import { nanoid } from '@reduxjs/toolkit';
import { SpatialSubdivisionParam, fluxZipFile } from 'helper/flux/automatic';
import { apiUrl, downloadFromUrl, saveBlob } from 'helper/downloadFile';
import { setFileDownloadControls } from 'helper/fileDownloadControls';
import formatBytes from 'helper/formatBytes';
import sendErrorToSentry, { sendInterfaceErrorToSentry } from 'helper/sendErrorToSentry';
import handleAtuthorizationErrors from 'helper/tokenError';
import { ResponseError } from 'services/api/base';
import { useGetZipLinkMutation } from 'services/api/products/flux';
import { downloadFaDailyZip } from 'services/fileDownloadApi';
import { FluxAutomaticLayoutTabs } from 'redux/reducers/fluxAutomaticLayoutReducer';
import { MltOptions } from 'redux/reducers/fluxAutomaticControlPanelReducer';
import {
  downloadDismissed,
  downloadFailed,
  downloadPreparing,
  downloadProgressed,
  downloadStarted,
} from 'redux/reducers/fileDownloadsReducer';
import { Stack } from 'react-bootstrap';
import ActionButton from 'components/ActionButton';
import { DOWNLOAD_ERROR_MESSAGE } from 'utils/hooks/useFileDownloadApi';
import ConfirmationDialogBox from '../../../../components/ConfirmationDialogBox';

export interface FluxAutomaticDOwnloadParams {
  review?: string
  models: string[]
  unidade?: MltOptions
  pastSimulationNames: string[]
  type: FluxAutomaticLayoutTabs
  spatialSubdivisionParam: SpatialSubdivisionParam
  forecastDate?: string
  acomphDate?: string
}

// Backend sem a rota `get-zip-link` (QA antigo, rollback para a 8.15.x, FE publicado antes do BE):
// o handler de NotFound do WSGI responde HTTP 200 com envelope `code: 404`
// ("Pagina nao encontrada."), que o baseQuery normaliza para `status: 404`. O `get-zip-link` só
// emite 200/400/429/500, então qualquer 404 aqui significa rota ausente.
const isZipLinkRouteMissing = (error: unknown) => (
  (error as Partial<ResponseError> | undefined)?.status === 404
);

export const TOO_MANY_DOWNLOADS_MESSAGE = 'Muitos downloads em sequência. Aguarde 1 minuto e tente de novo.';
export const PREPARE_ERROR_MESSAGE = 'Não foi possível preparar o arquivo. Verifique sua conexão e tente novamente.';

// Status numérico = o servidor respondeu (envelope com `code`); string = erro de transporte
// (FETCH_ERROR, PARSING_ERROR...), cujo texto técnico não serve para o usuário.
export function zipDownloadErrorMessage(error: ResponseError) {
  if (+error.status === 429) return TOO_MANY_DOWNLOADS_MESSAGE;
  if (typeof error.status !== 'number') return PREPARE_ERROR_MESSAGE;
  return error.description || error.data || PREPARE_ERROR_MESSAGE;
}

const modelsText = (count: number) => `${count} ${count === 1 ? 'modelo' : 'modelos'}`;

export const layoutCorrespondence = {
  daily: 'diaria',
  monthly: 'mensal',
  revision: 'revisao',
  unicenario: 'unicenario',
  classic: 'revisao',
} as const;

export default function useFluxAutomaticZipDownload({
  review,
  models,
  unidade,
  spatialSubdivisionParam,
  type,
  pastSimulationNames,
  forecastDate = 'latest',
  acomphDate = 'latest',
}: FluxAutomaticDOwnloadParams) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBlobLoading, setIsBlobLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [getZipLink, { isLoading: isLinkLoading }] = useGetZipLinkMutation();
  const zipParams = {
    models,
    review,
    unidade,
    version: 'latest',
    data_acomph: acomphDate,
    data_previsao: forecastDate,
    spatialSubdivisionParam,
    type: layoutCorrespondence[type],
  };
  const isLoading = isLinkLoading || isBlobLoading;
  const selectedModels = models.filter((model) => model);

  const enableZipButton = selectedModels.length > 0;

  // Erro com resposta do servidor: sessão vencida/sem permissão seguem o fluxo de sempre; o resto
  // vira o card de erro e o usuário continua na página (antes ia para /error e perdia o painel).
  const failWithResponse = (id: string, error: ResponseError) => {
    if (handleAtuthorizationErrors(error, navigate)) {
      dispatch(downloadDismissed(id));
      return;
    }
    sendErrorToSentry(error);
    dispatch(downloadFailed({ id, mensagem: zipDownloadErrorMessage(error) }));
  };

  // Caminho antigo (arquivo inteiro na memória da página): só como reserva se a rota nova faltar.
  // Como o arquivo passa pela página, dá para mostrar a % real e cancelar de verdade.
  const downloadAsBlob = async (id: string, filename: string, retry: () => void) => {
    const controller = new AbortController();
    let lastProgress: number | null | undefined;

    setFileDownloadControls(id, { retry, cancel: () => controller.abort() });
    dispatch(downloadProgressed({ id, progresso: 0 }));
    setIsBlobLoading(true);

    try {
      const result = await downloadFaDailyZip(zipParams, {
        signal: controller.signal,
        onDownloadProgress: ({ loaded, total }) => {
          // Sem Content-Length não há %: o card mostra a barra indeterminada
          const progresso = total ? Math.floor((loaded / total) * 100) : null;
          // Um evento por pedaço recebido: só despacha quando a % muda
          if (progresso === lastProgress) return;
          lastProgress = progresso;
          dispatch(downloadProgressed({ id, progresso, tamanhoBytes: total }));
        },
      });

      if (!(result instanceof Blob)) {
        failWithResponse(id, result);
        return;
      }
      saveBlob(result, filename);
      // Aqui o arquivo já veio inteiro: o navegador só grava, então é "concluído", não "iniciado"
      dispatch(downloadStarted({
        id,
        tamanhoBytes: result.size,
        mensagem: `Download concluído: ${formatBytes(result.size)}. O arquivo está na barra de downloads do navegador.`,
      }));
    } catch (error) {
      if (axios.isCancel(error)) {
        dispatch(downloadDismissed(id));
        return;
      }
      sendInterfaceErrorToSentry('Falha no download de arquivo', filename, error, {
        fingerprint: ['file-download-failed'],
      });
      dispatch(downloadFailed({ id, mensagem: DOWNLOAD_ERROR_MESSAGE }));
    } finally {
      setIsBlobLoading(false);
    }
  };

  // O servidor monta o ZIP e devolve um link curto; o navegador baixa direto no disco, com a barra
  // de progresso nativa (1,4 GB no SMAP-HISTORICO da Base Mensal não passam mais pela página).
  // O andamento vai para o card global (`fileDownloads`), que sobrevive à troca de aba do Flux.
  const downloadZip = async () => {
    const id = nanoid();
    const filename = fluxZipFile(type);
    const retry = () => { downloadZip(); };

    setIsModalOpen(false);
    setFileDownloadControls(id, { retry });
    dispatch(downloadPreparing({
      id,
      nome: filename,
      mensagem: `Preparando o arquivo no servidor… (${modelsText(selectedModels.length)})`,
    }));

    try {
      const link = await getZipLink({ ...zipParams, nomeArquivo: filename }).unwrap();
      // Sucesso sem `path` (ex.: sessão en-us, sem as mensagens do ena_diaria: corpo vazio):
      // o caminho antigo ainda funciona para esse usuário.
      if (!link?.path) {
        await downloadAsBlob(id, filename, retry);
        return;
      }
      downloadFromUrl(apiUrl(link.path));
      dispatch(downloadStarted({ id, tamanhoBytes: link.file_size_bytes ?? null }));
    } catch (error) {
      if (isZipLinkRouteMissing(error)) {
        await downloadAsBlob(id, filename, retry);
        return;
      }
      failWithResponse(id, error as ResponseError);
    }
  };

  function DownloadConfirmationDialog() {
    return (
      <ConfirmationDialogBox
        open={isModalOpen}
        dismiss={() => setIsModalOpen(false)}
        confirm={downloadZip}
      >
        <Stack>
          <p>
            Você quer mesmo baixar o arquivo
            {' '}
            <b>{fluxZipFile(type)}</b>
            ?
          </p>
          <p>
            Modelos selecionados:
            <b>
              {[...models, ...pastSimulationNames].join(', ')}
            </b>
          </p>
          <p className="mb-0">
            Arquivos com muitos modelos passam de 1 GB. A preparação leva alguns segundos e depois
            o download começa no navegador.
          </p>
        </Stack>
      </ConfirmationDialogBox>
    );
  }

  function DownloadZipButton() {
    return (
      <ActionButton
        type="primary"
        loading={isLoading}
        onClick={() => setIsModalOpen(true)}
        disabled={!enableZipButton}
        icon="download"
        small={false}
        tooltipText={isLinkLoading ? 'Preparando o arquivo…' : ''}
      />
    );
  }

  return {
    DownloadZipButton,
    DownloadConfirmationDialog,
  };
}
